<template>
  <v-container fluid>
    <v-card class="d-flex justify-center" flat v-if="loading.value">
      <v-card class="py-4" width="600" flat>
        <v-card-text>
          {{ loading.text }}
          <v-progress-linear color="secondary" striped :value="loading.value"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-card>
    <template v-else>
      <v-row dense>
        <v-col sm="3" v-if="page === 'sse'">
          <v-select
            v-if="page === 'sse'"
            v-model="computedSettings.project"
            label="Project"
            :items="projectItems"
            dense
          />
        </v-col>
        <v-col>
          <v-btn-toggle v-model="computedSettings.viewMode" mandatory color="primary" dense>
            <v-tooltip top v-for="button in computedRadioButton" :key="button.value">
              <template #activator="{ on }">
                <v-btn v-on="on" text :value="button.value" small>
                  {{ button.label }}
                </v-btn>
              </template>
              <span>Display {{ button.label }} results</span>
            </v-tooltip>
          </v-btn-toggle>
        </v-col>
        <template v-if="page === 'pf' && dates && settings.viewMode === 'timeSeries'">
          <v-spacer />
          <v-btn-toggle v-model="computedSettings.pfTimeSeriesOutputType" mandatory dense>
            <v-tooltip top>
              <template #activator="{ on }">
                <v-btn v-on="on" text value="dashboard">
                  <v-icon>mdi-view-dashboard</v-icon>
                </v-btn>
              </template>
              <span>Dashboard</span>
            </v-tooltip>
            <v-tooltip top>
              <template #activator="{ on }">
                <v-btn v-on="on" text value="tabular">
                  <v-icon>mdi-view-list</v-icon>
                </v-btn>
              </template>
              <span>Tabular</span>
            </v-tooltip>
          </v-btn-toggle>
        </template>
      </v-row>
      <v-row v-if="dates && ['daily', 'monthly'].includes(settings.viewMode)" dense>
        <v-col sm="3" class="py-0" v-if="['daily', 'monthly'].includes(settings.viewMode)">
          <v-select
            v-model="computedSettings.selectedYear"
            label="Year"
            :items="yearItems"
            multiple
          />
        </v-col>
      </v-row>
      <v-row v-if="dates && settings.viewMode === 'timeSeries'" dense class="mt-0">
        <v-col sm="3" class="py-0">
          <v-radio-group v-model="dateMode" :disabled="settings.viewMode !== 'timeSeries'" row>
            <v-radio label="Single Day" value="single"></v-radio>
            <v-radio label="Range" value="range"></v-radio>
          </v-radio-group>
        </v-col>
        <v-col sm="3" class="py-0">
          <InputDateRange
            v-if="dateMode === 'range'"
            v-model="computedDates"
            label="Dates"
            :disabled="settings.viewMode !== 'timeSeries'"
          />
          <InputDate
            v-else
            v-model="computedDates[0]"
            label="Date"
            :disabled="settings.viewMode !== 'timeSeries'"
            @input="setDates"
          />
        </v-col>
        <v-col sm="6" cols="12" class="py-0">
          <InputDatePlayControls
            v-if="dateMode === 'single'"
            v-model="computedDates[0]"
            :playMode.sync="playMode"
            :time-interval="settings.timeInterval"
            @input="setDates"
            :disabled="settings.viewMode !== 'timeSeries'"
          />
        </v-col>
      </v-row>
      <v-divider class="mb-4" />
      <v-row
        dense
        v-if="
          settings.viewMode !== 'violationReport' &&
          settings.viewMode !== 'contingency' &&
          disableResourceItem
        "
      >
        <template v-for="(items, key) in resourcesItems">
          <v-col sm="3" :key="key" v-if="items.length">
            <v-select
              v-model="computedSettings[key]"
              :label="selectLabel(key)"
              :items="items"
              multiple
              dense
            />
          </v-col>
        </template>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import { DateTime } from "luxon";
import { titleCase } from "@/plugins/utils";

export default {
  name: "OutputTimeSeriesToolbar",
  components: {
    InputDate: () => import("@/components/InputDate.vue"),
    InputDateRange: () => import("@/components/InputDateRange.vue"),
    InputDatePlayControls: () => import("@/components/InputDatePlayControls.vue"),
  },
  mixins: [],
  props: {
    settings: { type: Object, required: true },
    dates: { type: Array, required: true },
    resourcesItems: { type: Object, required: true },
    projectItems: { type: Array, required: false },
    yearItems: { type: Array, required: false },
    page: { type: String, required: true },
    loading: {
      type: Object,
      required: false,
      default: () => ({ value: 0, text: "" }),
    },
  },
  data() {
    return {
      dateMode: "single",
      playMode: "daily",
    };
  },
  computed: {
    disableResourceItem() {
      if (this.page === "pf") {
        return this.settings.pfTimeSeriesOutputType === "dashboard";
      }
      return true;
    },
    computedRadioButton() {
      const button =
        this.page === "sse"
          ? [
              {
                label: "Annual",
                value: "annual",
              },
            ]
          : [
              {
                label: "Violation Report",
                value: "violationReport",
              },
            ];
      if (this.settings.hasContingency) {
        button.push({
          label: "Contingency",
          value: "contingency",
        });
      }
      if (this.settings.hasMonthly) {
        button.push({
          label: "Monthly",
          value: "monthly",
        });
      }
      if (this.settings.hasDaily) {
        button.push({
          label: "Daily",
          value: "daily",
        });
      }
      if (this.settings.hasTimeseries) {
        button.push({
          label: "Time Series",
          value: "timeSeries",
        });
      }
      return button;
    },

    computedSettings: {
      get() {
        return this.settings;
      },
      set(val) {
        this.$emit("update:settings", val);
      },
    },
    computedDates: {
      get() {
        return this.dates;
      },
      set(val) {
        this.setDates(val);
      },
    },
  },
  watch: {
    dateMode() {
      const datesCopy = this.dates;
      datesCopy[0] = DateTime.fromISO(this.dates[0]).toISODate();
      datesCopy[1] = DateTime.fromISO(this.dates[1]).toISODate();
      this.$emit("update:dates", datesCopy);
    },
  },
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    titleCase,
    setDates(val) {
      const datesCopy = this.dates;
      if (Array.isArray(val)) {
        datesCopy.splice(0, 2, ...val);
      } else {
        datesCopy.splice(0, 2, ...Array(2).fill(val));
      }
      this.$emit("update:dates", datesCopy);
    },
    selectLabel(key) {
      const customLabels = {
        ren_gen_results: "Renewable Generators",
        renGens: "Renewable Generators",
        renewable_generators: "Renewable Generators",
        thermalGens: "Thermal Generators",
        thermal_generators: "Thermal Generators",
        gen: "Generator (PV or Slack)",
        sgen: "Generator (PQ)",
        bus: "Node",
        load: "Demand",
        storage: "Storage",
        switch: "Switch",
        line: "Branch",
        trafo: "Two-Winding Transformer",
        trafo3w: "Three-Winding Transformer",
      };
      return customLabels[key] || this.titleCase(key.replace("_results", ""));
    },
  },
};
</script>

<style></style>
